import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../lib/api";

const EMPTY_POST = { slug: "", title: "", excerpt: "", body_markdown: "", hero_image_url: "", tags: "", status: "draft", seo_title: "", seo_description: "" };
const EMPTY_DEADLINE = { slug: "", title: "", date: "", jurisdiction: "federal", audience: "all", summary: "", details_markdown: "", status: "published" };

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

function BlogAdmin() {
  const qc = useQueryClient();
  const { data: posts = [] } = useQuery({ queryKey: ["blog-admin"], queryFn: () => api.get("/blog/all").then((r) => r.data) });
  const [editing, setEditing] = useState(null); // null | 'new' | post
  const [form, setForm] = useState(EMPTY_POST);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setForm(EMPTY_POST); setEditing("new"); };
  const openEdit = (p) => {
    setForm({ ...p, tags: (p.tags || []).join(", ") });
    setEditing(p);
  };
  const close = () => { setEditing(null); setForm(EMPTY_POST); };

  const save = useMutation({
    mutationFn: (payload) => {
      const body = { ...payload, tags: payload.tags ? payload.tags.split(",").map((s) => s.trim()).filter(Boolean) : [] };
      if (editing === "new") return api.post("/blog", body).then((r) => r.data);
      return api.patch(`/blog/${editing.id}`, body).then((r) => r.data);
    },
    onSuccess: () => { toast.success(editing === "new" ? "Post created" : "Post updated"); qc.invalidateQueries({ queryKey: ["blog-admin"] }); close(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/blog/${id}`),
    onSuccess: () => { toast.success("Post deleted"); qc.invalidateQueries({ queryKey: ["blog-admin"] }); },
  });

  if (editing) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-6" data-testid="blog-editor">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-medium">{editing === "new" ? "New blog post" : `Editing: ${editing.title}`}</h3>
          <button className="text-xs text-zinc-500 hover:text-zinc-900" onClick={close} data-testid="blog-editor-close">Close</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">Title*</span>
            <input value={form.title} onChange={set("title")} className="border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="blog-editor-title" />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">Slug (URL)</span>
            <input value={form.slug} onChange={set("slug")} placeholder="auto from title" className="border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="blog-editor-slug" />
          </label>
        </div>
        <label className="flex flex-col gap-1 text-xs mt-3">
          <span className="font-medium">Excerpt (2-3 sentences, shown on cards + share previews)</span>
          <textarea value={form.excerpt} onChange={set("excerpt")} rows="2" className="border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="blog-editor-excerpt" />
        </label>
        <label className="flex flex-col gap-1 text-xs mt-3">
          <span className="font-medium">Body (Markdown supported — headings ##, lists, **bold**, [links](url))</span>
          <textarea value={form.body_markdown} onChange={set("body_markdown")} rows="14" className="border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono" data-testid="blog-editor-body" />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">Hero image URL (optional)</span>
            <input value={form.hero_image_url} onChange={set("hero_image_url")} placeholder="https://..." className="border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="blog-editor-image" />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">Tags (comma-separated)</span>
            <input value={form.tags} onChange={set("tags")} placeholder="tax-strategy, s-corp" className="border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="blog-editor-tags" />
          </label>
        </div>
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer font-medium text-zinc-700">SEO overrides (optional)</summary>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <input value={form.seo_title} onChange={set("seo_title")} placeholder="SEO title (defaults to post title)" className="border border-zinc-200 rounded-md px-3 py-2 text-sm" />
            <input value={form.seo_description} onChange={set("seo_description")} placeholder="Meta description (defaults to excerpt)" className="border border-zinc-200 rounded-md px-3 py-2 text-sm" />
          </div>
        </details>
        <div className="flex items-center gap-3 mt-5">
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.status === "published"} onChange={(e) => setForm({ ...form, status: e.target.checked ? "published" : "draft" })} data-testid="blog-editor-publish" />
            <span>Publish (visible at /blog and included in sitemap)</span>
          </label>
          <div className="ml-auto flex gap-2">
            <button className="px-4 py-2 text-xs border border-zinc-200 rounded-md hover:bg-zinc-50" onClick={close}>Cancel</button>
            <button
              disabled={save.isPending || !form.title || !form.body_markdown}
              className="px-4 py-2 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-40"
              onClick={() => save.mutate(form)}
              data-testid="blog-editor-save"
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-zinc-200 bg-white" data-testid="blog-admin">
      <div className="flex items-center justify-between p-4 border-b border-zinc-100">
        <h3 className="text-sm font-medium">Blog posts <span className="text-zinc-400 font-normal">({posts.length})</span></h3>
        <button onClick={openNew} className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700" data-testid="blog-new-btn">+ New post</button>
      </div>
      <div>
        {posts.length === 0 && <p className="text-xs text-zinc-400 italic p-6 text-center">No posts yet — click "New post" to start.</p>}
        {posts.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-4 border-b border-zinc-50 hover:bg-zinc-50/60" data-testid={`blog-row-${p.slug}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-zinc-900 truncate">{p.title}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                  {p.status === "published" ? "Published" : "Draft"}
                </span>
              </div>
              <p className="text-[11px] text-zinc-500 mt-0.5">/blog/{p.slug} · Updated {fmtDate(p.updated_at)}</p>
            </div>
            {p.status === "published" && (
              <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-emerald-700 hover:text-emerald-900 font-medium">View →</a>
            )}
            <button onClick={() => openEdit(p)} className="text-[11px] text-zinc-700 hover:text-zinc-900" data-testid={`blog-edit-${p.slug}`}>Edit</button>
            <button onClick={() => { if (confirm(`Delete "${p.title}"?`)) del.mutate(p.id); }} className="text-[11px] text-red-600 hover:text-red-800" data-testid={`blog-delete-${p.slug}`}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DeadlinesAdmin() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["deadlines-admin"], queryFn: () => api.get("/deadlines/all").then((r) => r.data) });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_DEADLINE);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = useMutation({
    mutationFn: (payload) => {
      if (editing === "new") return api.post("/deadlines", payload).then((r) => r.data);
      return api.patch(`/deadlines/${editing.id}`, payload).then((r) => r.data);
    },
    onSuccess: () => { toast.success(editing === "new" ? "Deadline added" : "Deadline updated"); qc.invalidateQueries({ queryKey: ["deadlines-admin"] }); setEditing(null); setForm(EMPTY_DEADLINE); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/deadlines/${id}`),
    onSuccess: () => { toast.success("Deadline deleted"); qc.invalidateQueries({ queryKey: ["deadlines-admin"] }); },
  });

  if (editing) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-6" data-testid="deadline-editor">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-medium">{editing === "new" ? "New deadline" : `Editing: ${editing.title}`}</h3>
          <button className="text-xs text-zinc-500" onClick={() => setEditing(null)}>Close</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">Title*</span>
            <input value={form.title} onChange={set("title")} className="border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="deadline-editor-title" />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">Date* (YYYY-MM-DD)</span>
            <input type="date" value={form.date} onChange={set("date")} className="border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="deadline-editor-date" />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">Jurisdiction</span>
            <select value={form.jurisdiction} onChange={set("jurisdiction")} className="border border-zinc-200 rounded-md px-3 py-2 text-sm">
              <option value="federal">Federal</option>
              <option value="state">State</option>
              <option value="international">International</option>
              <option value="firm">Firm</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">Audience</span>
            <select value={form.audience} onChange={set("audience")} className="border border-zinc-200 rounded-md px-3 py-2 text-sm">
              <option value="all">Everyone</option>
              <option value="individuals">Individuals</option>
              <option value="businesses">Businesses</option>
              <option value="non-residents">Non-residents</option>
            </select>
          </label>
        </div>
        <label className="flex flex-col gap-1 text-xs mt-3">
          <span className="font-medium">Summary (one line, shown on card)</span>
          <input value={form.summary} onChange={set("summary")} className="border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="deadline-editor-summary" />
        </label>
        <label className="flex flex-col gap-1 text-xs mt-3">
          <span className="font-medium">Details (Markdown, optional)</span>
          <textarea value={form.details_markdown} onChange={set("details_markdown")} rows="6" className="border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono" />
        </label>
        <div className="flex items-center gap-3 mt-5">
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.status === "published"} onChange={(e) => setForm({ ...form, status: e.target.checked ? "published" : "draft" })} />
            <span>Publish (visible at /deadlines)</span>
          </label>
          <div className="ml-auto flex gap-2">
            <button className="px-4 py-2 text-xs border border-zinc-200 rounded-md" onClick={() => setEditing(null)}>Cancel</button>
            <button disabled={save.isPending || !form.title || !form.date} className="px-4 py-2 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-40" onClick={() => save.mutate(form)} data-testid="deadline-editor-save">
              {save.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-zinc-200 bg-white" data-testid="deadlines-admin">
      <div className="flex items-center justify-between p-4 border-b border-zinc-100">
        <h3 className="text-sm font-medium">Tax deadlines <span className="text-zinc-400 font-normal">({items.length})</span></h3>
        <button onClick={() => { setForm(EMPTY_DEADLINE); setEditing("new"); }} className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700" data-testid="deadline-new-btn">+ New deadline</button>
      </div>
      {items.length === 0 && <p className="text-xs text-zinc-400 italic p-6 text-center">No deadlines yet.</p>}
      {items.map((d) => (
        <div key={d.id} className="flex items-center gap-3 p-4 border-b border-zinc-50 hover:bg-zinc-50/60" data-testid={`deadline-row-${d.slug}`}>
          <div className="min-w-[110px] text-xs">
            <div className="font-medium text-zinc-900">{fmtDate(d.date)}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{d.jurisdiction}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-900 truncate">{d.title}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${d.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>{d.status}</span>
            </div>
            {d.summary && <p className="text-[11px] text-zinc-500 mt-0.5 truncate">{d.summary}</p>}
          </div>
          <button onClick={() => { setForm({ ...d, details_markdown: d.details_markdown || "" }); setEditing(d); }} className="text-[11px] text-zinc-700 hover:text-zinc-900">Edit</button>
          <button onClick={() => { if (confirm(`Delete "${d.title}"?`)) del.mutate(d.id); }} className="text-[11px] text-red-600 hover:text-red-800">Delete</button>
        </div>
      ))}
    </div>
  );
}

export default function ContentPage() {
  const [tab, setTab] = useState("blog");
  return (
    <div className="max-w-5xl mx-auto py-8 px-4" data-testid="content-admin-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900">Content</h1>
        <p className="text-sm text-zinc-500 mt-1">Publish blog posts and tax deadlines. Published items appear on <code className="text-xs bg-zinc-100 px-1 rounded">/blog</code>, <code className="text-xs bg-zinc-100 px-1 rounded">/deadlines</code>, and Google's news + sitemap indexes.</p>
      </div>
      <div className="flex gap-2 border-b border-zinc-200 mb-6">
        <button onClick={() => setTab("blog")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "blog" ? "border-emerald-600 text-emerald-700" : "border-transparent text-zinc-500 hover:text-zinc-900"}`} data-testid="content-tab-blog">Blog posts</button>
        <button onClick={() => setTab("deadlines")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "deadlines" ? "border-emerald-600 text-emerald-700" : "border-transparent text-zinc-500 hover:text-zinc-900"}`} data-testid="content-tab-deadlines">Deadlines</button>
      </div>
      {tab === "blog" ? <BlogAdmin /> : <DeadlinesAdmin />}
    </div>
  );
}
